import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Beaker, CheckCircle2, ClipboardCheck, FlaskConical, Lock, PackageCheck, PenLine, Play, TestTube2, TriangleAlert, X,
  type LucideIcon,
} from 'lucide-react';
import { SAMPLE_STATUS_LABEL, SAMPLE_TYPE_LABEL } from '@dru/types';
import type { CursorPage, LabResultCounts, LabWorklistCounts, SampleRow } from '@dru/types';
import { LabStatusStrip } from '../components/LabStatusStrip';
import {
  BenchScanBox, WaitingOnLine, benchWaitingOn, classifyScan, scanRefusal, useCheckInHref, useScrollMemory, useVenueParam,
} from '../components/BenchNav';
import { BenchBackLink } from '../components/BenchNav';
import { useLabLinks } from '../lib/context';
import { countText } from '../lib/counts';
import {
  Alert, Button, Card, ConfirmDialog, DataTable, FilterBar, IconAction, IconButton, InfoTip, Pagination, RowActions, type Column, SelectField, StatusBadge, Steps, cn, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { formatDateTime } from '../../../lib/format';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { sampleJourney } from '../../../lib/sampleJourney';
import { useVenueFilter } from '../../hospital/usePartner';
import { VenuePicker } from '../../hospital/routes/Queue';

/**
 * Samples on the bench.
 *
 * The subject is a pseudonym and an age band, never a name. A laboratory needs
 * to know that this tube belongs to a 34-year-old male so the reference range
 * is the right one; it does not need to know which of the plant's welders that
 * is, and the LabWorkContext projection makes sure it cannot find out.
 *
 * The screen answers the bench's three questions in order: what needs me
 * (the tiles), which tube am I on and where is it (the panel's steps), and
 * what is the one valid next act (a single primary button). Other valid moves
 * are offered quieter, and a move this login cannot make is not offered.
 *
 * Rejection is never a dead end — it carries a coded reason and the API spawns
 * a linked recollection, so a rejected tube becomes a request for another one.
 */

type Group = { key: string; label: string; hint: string; states: string[]; icon: typeof Beaker; tone: 'attention' | 'quiet' | 'warning' };

/**
 * The tiles, worded for whoever is reading. "Yours to move" is true of the
 * bench and false of a pathologist, whose part of the journey is the tubes
 * waiting on a signature — so that is the tile that asks for their attention.
 */
function groupsFor(permissions: ReadonlySet<string>): Group[] {
  const bench = permissions.has('lab.sample-accession');
  const signs = permissions.has('lab.result-validate') || permissions.has('lab.result-release');
  return [
    { key: 'bench', label: 'At the bench', hint: bench ? 'Received or processing — yours to move' : 'Received or processing', states: ['RECEIVED', 'PROCESSING'], icon: Beaker, tone: bench ? 'attention' : 'quiet' },
    { key: 'arriving', label: 'Waiting to arrive', hint: 'Ordered, drawn or in transit', states: ['ORDERED', 'COLLECTED', 'IN_TRANSIT'], icon: TestTube2, tone: 'quiet' },
    { key: 'pathologist', label: signs ? 'Waiting for you' : 'With the pathologist', hint: signs ? 'Resulted or validated, to sign off' : 'Resulted or validated, not released', states: ['RESULTED', 'VALIDATED'], icon: FlaskConical, tone: signs ? 'attention' : 'quiet' },
    { key: 'rejected', label: 'Rejected · recollect', hint: 'Rejected with a reason; another draw requested', states: ['REJECTED', 'RECOLLECT_REQUESTED'], icon: TriangleAlert, tone: 'warning' },
  ];
}

const STATES = ['ORDERED', 'COLLECTED', 'IN_TRANSIT', 'RECEIVED', 'PROCESSING', 'RESULTED', 'VALIDATED',
  'RELEASED', 'REJECTED', 'RECOLLECT_REQUESTED'];

/** The lab screens sit beside this one in whichever console it is mounted. */

export function Worklist() {
  const { permissions } = useAuth();
  const venueFilter = useVenueFilter();
  const { partner, setPartner, search: venueSearch, query, fixed } = venueFilter;
  // Kept in the address so the next screen, and Back, open on the same venue.
  const venueId = useVenueParam(venueFilter);
  const checkInHref = useCheckInHref(permissions);
  // Status, search and page live in the address, so Back and a shared link
  // land on the same view.
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const term = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  // Built from the live address, not the render's copy: a debounced search
  // firing a moment later must not write back an address without the tube
  // that was opened in between.
  const live = () => new URLSearchParams(window.location.search);
  const setParam = (k: string, v: string) => {
    const n = live(); if (v) n.set(k, v); else n.delete(k);
    if (k !== 'page') n.delete('page');
    setParams(n, { replace: true });
  };
  const setStatus = (v: string) => setParam('status', v);
  const setPage = (n: number) => setParam('page', n > 1 ? String(n) : '');
  const [search, setSearch] = useState(term);
  // A link or Back that changes the address updates the box too, before
  // the box's own debounce could write its stale text back over it.
  useEffect(() => { setSearch(term); }, [term]);
  // Typing searches after a pause; an employee card or a pass never does —
  // they are named for what they are instead (see classifyScan).
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const c = classifyScan(search);
      if (c.kind === 'employee' || c.kind === 'pass') return;
      const want = c.kind === 'subject' ? c.code : search.trim();
      if (want !== (live().get('q') ?? '')) setParam('q', want);
    }, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  // Arriving from a report or a review with `?subject=<encounter>`: narrow the
  // list to that subject's tubes by its pseudonymous code (the list is
  // searched by code, never by name), then drop the id from the address.
  const subjectParam = params.get('subject') ?? '';
  useEffect(() => {
    if (!subjectParam) return;
    let alive = true;
    api.get<{ subject: { displayName: string } }>(`/lab/encounters/${subjectParam}/battery`)
      .then((b) => {
        if (!alive) return;
        const code = b.subject.displayName.replace(/^Subject\s+/i, '');
        const n = live(); n.delete('subject'); if (code && !n.get('q')) n.set('q', code); n.delete('page');
        setParams(n, { replace: true });
      })
      .catch(() => { if (alive) { const n = live(); n.delete('subject'); setParams(n, { replace: true }); } });
    return () => { alive = false; };
  }, [subjectParam]); // eslint-disable-line react-hooks/exhaustive-deps
  const [nonce, setNonce] = useState(0);
  // The chosen tube lives in the address too: a phone's Back closes the
  // detail view and lands on the same filtered list.
  const selectedId = params.get('sample');
  const { remember, restore } = useScrollMemory(true);
  const setSelectedId = (id: string | null) => {
    if (id) remember();
    const n = live(); if (id) n.set('sample', id); else n.delete('sample');
    setParams(n, { replace: !id });
  };
  // A notice is about the scan that raised it; a new search drops it.
  useEffect(() => { setNotice((n) => (n && /^No tube /.test(n) && openWhenFound === null ? n : null)); }, [term]); // eslint-disable-line react-hooks/exhaustive-deps
  // A scanned tube opens once the search has found it.
  const [openWhenFound, setOpenWhenFound] = useState<string | null>(null);
  const submitScan = () => {
    const c = classifyScan(search);
    const refusal = scanRefusal(c);
    setNotice(refusal);
    if (c.kind === 'employee' || c.kind === 'pass' || c.kind === 'empty') return;
    if (c.kind === 'specimen') setOpenWhenFound(c.code);
    setParam('q', c.code);
  };

  // Paged on the server, in the server's order (sent back first, then where
  // each tube stands, then when it was drawn), so page 2 is truly what
  // follows page 1 and the tiles count every tube, not the page loaded.
  const PAGE = 15;
  const qs = term ? `&q=${encodeURIComponent(term)}` : '';
  // A tube chosen under one venue or filter is not carried into another.
  // Compared with the scope it was chosen under, not "first run": effects run
  // twice in development, and a first-run flag read the second run as a
  // change and dropped the tube a Back link had just reopened.
  const scopeKey = `${query}|${status}|${term}`;
  const lastScope = useRef(scopeKey);
  useEffect(() => {
    if (lastScope.current === scopeKey) return;
    lastScope.current = scopeKey;
    if (live().get('sample')) setSelectedId(null);
  }, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const samples = useQuery<CursorPage<SampleRow>>(
    () => api.get<CursorPage<SampleRow>>(
      `/lab/worklist?limit=${PAGE}${query}${qs}${status ? `&status=${status}` : ''}&cursor=o:${(page - 1) * PAGE}`),
    [query, status, term, nonce, page],
  );
  // Same venue and search as the list, but not the status: a card picked
  // never zeroes the other cards.
  const counts = useQuery<LabWorklistCounts>(() => api.get(`/lab/worklist/counts?x=1${query}${qs}`), [query, term, nonce]);
  const resultCounts = useQuery<LabResultCounts>(() => api.get(`/lab/results/counts?x=1${query}`), [query, nonce]);
  const rows = samples.data?.items ?? [];
  const total = samples.data?.total ?? rows.length;
  const pageRows = rows;
  const selected = rows.find((s) => s.id === selectedId) ?? null;
  useEffect(() => {
    // Only once the search for that barcode has come back, not on the list
    // that was showing when the scan arrived.
    if (!openWhenFound || !samples.data || samples.loading || term.toUpperCase() !== openWhenFound) return;
    const hit = rows.find((r) => r.barcode.toUpperCase() === openWhenFound);
    setOpenWhenFound(null);
    if (hit) setSelectedId(hit.id);
    else setNotice(`No tube ${openWhenFound} in this venue.`);
  }, [samples.data, samples.loading, term]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => setNonce((n) => n + 1);
  const GROUPS = useMemo(() => groupsFor(permissions), [permissions]);

  // Below xl a chosen tube is its own view: the list steps aside, and
  // "Back to list" returns to the same filters, page and row.
  useEffect(() => {
    if (selected && !window.matchMedia('(min-width: 1280px)').matches) window.scrollTo({ top: 0 });
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeDetail = () => { setSelectedId(null); restore(); };

  return (
    <div className="space-y-4">
      <BenchBackLink className="-mb-2" />
      <PageHeader
        title="Samples"
        hint="Where each tube is, and the next step for it. Subjects are pseudonymous — names never reach the bench."
        actions={<Button variant="secondary" size="sm" onClick={refresh}>Refresh</Button>}
      />

      {selected && (
        // Tablet and phone: the chosen tube, full width, and the way back.
        <div className="space-y-3 xl:hidden">
          <Button variant="secondary" size="sm" icon={<ArrowLeft aria-hidden className="h-4 w-4" />} onClick={closeDetail}>
            Back to list
          </Button>
          <SamplePanel sample={selected} siblings={rows} permissions={permissions} onDone={refresh} onClose={closeDetail} venueId={venueId} />
        </div>
      )}

      <div className={cn('space-y-4', selected && 'max-xl:hidden')}>
      <LabStatusStrip tubes={counts.data?.tubes} results={resultCounts.data}
                      canSign={permissions.has('lab.result-validate') || permissions.has('lab.result-release')} />

      {/* What needs attention, counted on the server over every tube in
          scope. Each tile filters. */}
      {(
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" aria-label="What needs attention">
          {GROUPS.map((g) => {
            // Unknown while loading or failed: "—", never a zero.
            const known = counts.data ? g.states.reduce((n, st) => n + (counts.data!.tubes.byStatus[st] ?? 0), 0) : undefined;
            const count = known ?? 0;
            const active = status === g.states.join(',');
            const more = false;
            const Icon = g.icon;
            return (
              <button
                key={g.key}
                type="button"
                aria-pressed={active}
                onClick={() => setStatus(active ? '' : g.states.join(','))}
                className={cn(
                  'rounded-card border bg-white px-3 py-2.5 text-left shadow-card transition-colors duration-hover hover:bg-neutral-50',
                  active ? 'border-primary-600 ring-1 ring-primary-600'
                    : g.tone === 'attention' && count > 0 ? 'border-primary-300'
                      : g.tone === 'warning' && count > 0 ? 'border-warning-600/40' : 'border-neutral-200',
                )}
              >
                <div className={cn('flex items-center gap-1.5 text-caption',
                  g.tone === 'warning' && count > 0 ? 'text-warning-600'
                    : g.tone === 'attention' && count > 0 ? 'text-primary-700' : 'text-neutral-500')}
                >
                  <Icon aria-hidden className="h-4 w-4" /> {g.label}
                </div>
                <div className="mt-0.5 text-h3 tabular-nums text-neutral-900">{countText(known)}{more ? '+' : ''}</div>
                <div className="text-caption font-normal text-neutral-500">{g.hint}</div>
              </button>
            );
          })}
        </div>
      )}

      <FilterBar
        filters={[{
          key: 'status', label: 'State', allLabel: 'All states',
          // The tiles' groups first, so a tile's filter reads back as its
          // name in the chip rather than as a list of states.
          options: [
            ...GROUPS.map((g) => ({ value: g.states.join(','), label: g.label })),
            ...STATES.map((s) => ({ value: s, label: SAMPLE_STATUS_LABEL[s] ?? s })),
          ],
        }]}
        value={{ status: status || undefined }}
        onChange={(v) => setStatus(v.status ?? '')}
      >
        {/* Clearing it means every venue, which is the default and the
            right one for a bench: a sample is drawn where the encounter
            ran and resulted wherever the tube lands. */}
        <VenuePicker partner={partner} onChange={setPartner} search={venueSearch} fixed={fixed} />
        <BenchScanBox value={search} onChange={(v) => { setSearch(v); setNotice(null); }} onSubmit={submitScan}
                      placeholder="Scan tube, or type barcode / subject code"
                      label="Scan a tube barcode, or type a barcode or subject code" className="w-full sm:w-80" />
      </FilterBar>
      {notice && (
        <div role="status" className="flex flex-wrap items-center gap-2 rounded-card border border-warning-600/40 bg-warning-100 px-3 py-2 text-small text-warning-600">
          <span className="min-w-0 flex-1">{notice}</span>
          {checkInHref && /ID card|appointment pass/.test(notice) && (
            <Link to={checkInHref} className="font-medium underline">Open the check-in desk</Link>
          )}
          <button type="button" className="text-caption underline" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}
      {(term || status) && (
        <p className="text-caption text-neutral-600" aria-live="polite">
          {samples.data ? `${countText(total)} matching` : 'Searching…'}
          {term && <> for “{term}”</>}
        </p>
      )}

      {/* The table has the full width until a tube is chosen; then its steps
          open beside it. An empty side panel squeezed barcodes onto three lines. */}
      <div className={cn('grid items-start gap-4', selected && 'xl:grid-cols-[minmax(0,1fr)_24rem]')}>
        <div className="min-w-0 space-y-3">
          {!selected && rows.length > 0 && (
            <p className="text-caption text-neutral-500">Choose a sample to see its progress and the next valid step.</p>
          )}
          <DataTable<SampleRow>
            rows={pageRows}
            loading={samples.loading}
            error={samples.error}
            onRetry={samples.reload}
            rowKey={(s) => s.id}
            onRowClick={(s) => setSelectedId(s.id)}
            empty={term ? {
              title: `No tube or subject matches “${term}”`,
              hint: 'Check the barcode, or clear the search to see every tube in this venue.',
              action: <Button variant="secondary" size="sm" onClick={() => { setSearch(''); setParam('q', ''); }}>Clear search</Button>,
            } : status ? {
              title: 'No tubes in this state',
              hint: 'Nothing is in this state right now.',
              action: <Button variant="secondary" size="sm" onClick={() => setStatus('')}>Show all states</Button>,
            } : {
              title: 'No tubes yet',
              hint: 'A tube appears here once phlebotomy draws it. Scan a tube barcode above to find one.',
            }}
            // With the panel open the table is narrower: the collection time
            // gives way (it is in the panel) so Next step stays in view.
            columns={([
              {
                key: 'sample', header: 'Sample', alwaysVisible: true,
                cell: (s) => (
                  <div className={cn('min-w-0', s.id === selectedId && 'border-l-2 border-primary-600 pl-2')}>
                    <div className="whitespace-nowrap font-mono text-small text-neutral-900">{s.barcode}</div>
                    <div className="text-caption font-normal text-neutral-500">
                      {SAMPLE_TYPE_LABEL[s.sampleType] ?? s.sampleType}
                      <span className="md:hidden"> · {s.subject.displayName}</span>
                    </div>
                  </div>
                ),
              },
              {
                key: 'subject', header: 'Subject', hideOnMobile: true,
                cell: (s) => (
                  <div>
                    <div className="whitespace-nowrap font-mono text-caption text-neutral-800">{s.subject.displayName}</div>
                    <div className="text-caption font-normal text-neutral-500">{subjectLine(s.subject)}</div>
                  </div>
                ),
              },
              {
                key: 'status', header: 'State', mobileLabel: false,
                cell: (s) => <SampleBadge sample={s} />,
              },
              {
                key: 'collected', header: 'Collected', align: 'right', hideOnMobile: true,
                cell: (s) => <span className="whitespace-nowrap text-caption font-normal tabular-nums text-neutral-600">{when(s.collectedAt)}</span>,
              },
              {
                key: 'next', header: 'Next step', align: 'right', alwaysVisible: true, mobileLabel: '',
                cell: (s) => <NextAct sample={s} permissions={permissions} onDone={refresh} venueId={venueId} />,
              },
            ] as Column<SampleRow>[]).filter((c) => !(selected && c.key === 'collected'))}
          />
          {total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Pagination page={page} pageSize={PAGE} total={total} disabled={samples.loading}
                          onPageChange={(n) => { setPage(n); setSelectedId(null); }} />
            </div>
          )}
        </div>

        {selected && (
          <aside className="hidden xl:sticky xl:top-20 xl:block" aria-label="Selected sample">
            <SamplePanel sample={selected} siblings={rows} permissions={permissions} onDone={refresh} onClose={closeDetail} venueId={venueId} />
          </aside>
        )}
      </div>
      </div>
    </div>
  );
}

/**
 * The row's one forward act, when this login can take it. Nothing else fits a row.
 *
 * A processing tube's next act is entering its values, not "Mark resulted":
 * that move is the manual path for results recorded elsewhere, and a column
 * of it one click from each row invited marking tubes resulted with nothing
 * entered. A pathologist's next act on a resulted tube is on their own
 * screen, so the row points there instead of saying they are waited on.
 */
/** The icon for each move a tube can be given from its row. */
const MOVE_ICON: Record<string, LucideIcon> = {
  COLLECTED: TestTube2,
  RECEIVED: PackageCheck,
  PROCESSING: Play,
  RESULTED: CheckCircle2,
};

function NextAct({ sample, permissions, onDone, venueId }: { sample: SampleRow; permissions: ReadonlySet<string>; onDone: () => void; venueId: string }) {
  const j = sampleJourney(sample.status, permissions);
  const links = useLabLinks();
  const nav = useNavigate();
  // The next screen opens on this subject, in this venue, with a way back here.
  const resultsTo = links.to('results', { subject: sample.encounterId, sample: sample.id, venue: venueId, back: true });
  const releaseTo = links.to('release', { subject: sample.encounterId, venue: venueId, back: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const review = (sample.status === 'RESULTED' && holds(permissions, 'lab.result-validate'))
    || (sample.status === 'VALIDATED' && holds(permissions, 'lab.result-release'));
  // A value sent back is owed again whatever the tube's state says.
  const reenter = (sample.sentBack ?? 0) > 0 && holds(permissions, 'lab.result-enter');
  if (reenter) {
    return (
      <RowActions>
        <IconAction
          label={`Re-enter results for ${sample.barcode}`}
          icon={PenLine}
          primary
          onClick={() => nav(resultsTo)}
        />
      </RowActions>
    );
  }
  if (j.enterResults || review) {
    return (
      <RowActions>
        <IconAction
          label={`${j.enterResults ? 'Enter results' : sample.status === 'RESULTED' ? 'Review to validate' : 'Review to release'} for ${sample.barcode}`}
          icon={j.enterResults ? PenLine : ClipboardCheck}
          primary
          onClick={() => nav(j.enterResults ? resultsTo : releaseTo)}
        />
      </RowActions>
    );
  }
  if (!j.primary) {
    // Whose move it is, by role — never a named person.
    const w = benchWaitingOn(sample.status, permissions);
    return (
      <span className="text-caption font-normal text-neutral-500">
        {w?.role ? `Next: ${w.role}` : (j.waitingOn ?? '—')}
      </span>
    );
  }
  const move = j.primary;
  return (
    <div className="flex flex-col items-end gap-1">
      <RowActions>
        <IconAction
          label={`${move.label}: ${sample.barcode}`}
          icon={MOVE_ICON[move.to] ?? Play}
          primary
          loading={busy}
          onClick={async () => {
            setBusy(true); setError(null);
            try { await transition(sample.id, move.to); onDone(); } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
          }}
        />
      </RowActions>
      {error && <span role="alert" className="text-caption text-danger-600">{error}</span>}
    </div>
  );
}

/** A tube's state, or that a value on it was sent back and is owed again. */
function SampleBadge({ sample: s }: { sample: SampleRow }) {
  if ((s.sentBack ?? 0) > 0) return <StatusBadge tone="warning">Sent back — re-enter</StatusBadge>;
  return <StatusBadge tone={sampleTone(s.status)}>{SAMPLE_STATUS_LABEL[s.status] ?? s.status}</StatusBadge>;
}

const ARRIVING = new Set(['COLLECTED', 'IN_TRANSIT']);

function SamplePanel({ sample, siblings, permissions, onDone, onClose, venueId }: {
  sample: SampleRow;
  venueId: string;
  /** The loaded tubes, for "receive all of this subject's tubes". */
  siblings: SampleRow[];
  permissions: ReadonlySet<string>;
  onDone: () => void;
  onClose: () => void;
}) {
  const j = sampleJourney(sample.status, permissions);
  // The same person's other tubes still on their way: received in one act
  // rather than one click per tube.
  const arrivingTogether = ARRIVING.has(sample.status) && permissions.has('lab.sample-accession')
    ? siblings.filter((x) => x.encounterId === sample.encounterId && ARRIVING.has(x.status)) : [];
  const [receivingAll, setReceivingAll] = useState(false);
  const links = useLabLinks();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('HAEMOLYSED');
  // Result entry opens on this subject and venue, with Back to this list.
  const resultsTo = links.to('results', { subject: sample.encounterId, sample: sample.id, venue: venueId, back: true });
  const canEnter = holds(permissions, 'lab.result-enter') && ['RECEIVED', 'PROCESSING', 'RESULTED'].includes(sample.status);
  // Entry leads when it is the next act; the manual move sits under it.
  const primary = j.enterResults ? null : j.primary;
  const quiet = [...(j.enterResults && j.primary ? [j.primary] : []), ...j.secondary];
  const signs = holds(permissions, ['lab.result-validate', 'lab.result-release']);
  // Said once, plainly, on the tubes where it matters: this login enters and
  // moves tubes, and the signature that follows is somebody else's.
  const notYours = !signs && ['RESULTED', 'VALIDATED'].includes(sample.status);

  async function go(to: string, why?: string) {
    setBusy(to); setError(null);
    try {
      await transition(sample.id, to, why);
      setRejecting(false); setReason('HAEMOLYSED');
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-4 py-3">
        <div className="min-w-0">
          <div className="text-caption text-neutral-500">Sample</div>
          <div className="font-mono text-h4 text-neutral-900">{sample.barcode}</div>
          <div className="text-small text-neutral-600">
            {SAMPLE_TYPE_LABEL[sample.sampleType] ?? sample.sampleType} · <span className="font-mono">{sample.subject.displayName}</span>
            {subjectLine(sample.subject) ? ` · ${subjectLine(sample.subject)}` : ''}
          </div>
        </div>
        <IconButton label="Close sample" size="sm" onClick={onClose}><X aria-hidden className="h-4 w-4" /></IconButton>
      </div>

      <div className="space-y-4 px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <SampleBadge sample={sample} />
          <span className="text-small text-neutral-700">
            {(sample.sentBack ?? 0) > 0
              ? `${sample.sentBack} value${sample.sentBack === 1 ? '' : 's'} sent back by the pathologist — enter again`
              : j.now}
          </span>
        </div>

        <Steps steps={j.steps} label="Sample progress" orientation="vertical" />

        <div className="space-y-2 border-t border-neutral-200 pt-3">
          <div className="text-caption text-neutral-500">Next step</div>
          {j.enterResults ? (
            <Link
              to={resultsTo}
              className="flex h-11 items-center justify-center rounded-btn bg-primary-600 text-body-lg font-semibold text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
            >
              Enter results for this tube
            </Link>
          ) : primary && arrivingTogether.length > 1 && primary.to === 'RECEIVED' ? (
            <Button
              className="w-full" size="lg" loading={receivingAll} disabled={busy !== null || receivingAll}
              onClick={async () => {
                setReceivingAll(true); setError(null);
                try {
                  await api.post(`/lab/encounters/${sample.encounterId}/samples/receive`, {}, crypto.randomUUID());
                  onDone();
                } catch (e) { setError(errorMessage(e)); } finally { setReceivingAll(false); }
              }}
            >
              Receive all {arrivingTogether.length} tubes of {sample.subject.displayName}
            </Button>
          ) : primary ? (
            <Button className="w-full" size="lg" loading={busy === primary.to} disabled={busy !== null} onClick={() => go(primary.to)}>
              {primary.label}
            </Button>
          ) : benchWaitingOn(sample.status, permissions) ? (
            <WaitingOnLine status={sample.status} permissions={permissions} className="text-small" />
          ) : (
            <p className="text-small text-neutral-700">{j.waitingOn ?? 'Nothing left to do for this tube.'}</p>
          )}
          {canEnter && !j.enterResults && (
            <Link
              to={resultsTo}
              className="flex h-10 items-center justify-center rounded-btn border border-neutral-300 bg-white text-body font-semibold text-neutral-800 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              {sample.status === 'RESULTED' ? 'See or re-enter results' : 'Enter results for this tube'}
            </Link>
          )}
          {quiet.map((m) => (
            <div key={m.to}>
              <Button className="w-full" variant="secondary" loading={busy === m.to} disabled={busy !== null} onClick={() => go(m.to)}>
                {m.label}
              </Button>
              {m.manual && (
                <p className="mt-1 flex items-center gap-1 text-caption font-normal text-neutral-500">
                  Only for results recorded elsewhere.
                  <InfoTip label="About marking a tube resulted">
                    Entering the last value marks the tube resulted on its own.
                  </InfoTip>
                </p>
              )}
            </div>
          ))}
          {notYours && (
            <p className="flex items-start gap-1.5 text-caption font-normal text-neutral-600">
              <Lock aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-400" />
              Validation and release are a pathologist’s. They are not offered on this login.
            </p>
          )}
          {j.rejectable && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => setRejecting(true)}
              className="w-full rounded-btn py-2 text-small font-medium text-danger-600 hover:bg-danger-100 disabled:text-neutral-500"
            >
              Reject this tube…
            </button>
          )}
          {error && <Alert tone="danger">{error}</Alert>}
        </div>

        {/* The times behind the steps, out of the way until wanted. */}
        <details className="border-t border-neutral-200 pt-3">
          <summary className="cursor-pointer text-small font-medium text-primary-700 max-md:min-h-10 max-md:py-2">View history</summary>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-caption">
            <dt className="text-neutral-500">Collected</dt><dd className="tabular-nums text-neutral-800">{sample.collectedAt ? when(sample.collectedAt) : 'Not yet'}</dd>
            <dt className="text-neutral-500">Received</dt><dd className="tabular-nums text-neutral-800">{sample.receivedAt ? when(sample.receivedAt) : 'Not yet'}</dd>
            <dt className="text-neutral-500">Now</dt><dd className="text-neutral-800">{SAMPLE_STATUS_LABEL[sample.status] ?? sample.status}</dd>
            {(sample.sentBack ?? 0) > 0 && (<><dt className="text-neutral-500">Sent back</dt><dd className="text-warning-600">{sample.sentBack} value{sample.sentBack === 1 ? '' : 's'}</dd></>)}
          </dl>
          <p className="mt-2 text-caption text-neutral-500">Collected is not received, and received is not released — each step is recorded separately.</p>
        </details>
      </div>

      {rejecting && (
        // A rejection spawns a recollection: the worker comes back for
        // another draw. A coded reason, and a confirmation, rather than a
        // free box beside a red button.
        <ConfirmDialog
          title={`Reject ${sample.barcode}?`}
          what="The tube is marked rejected and a recollection is requested. The employee is asked for another sample."
          affects={`${sample.subject.displayName} · ${SAMPLE_TYPE_LABEL[sample.sampleType] ?? sample.sampleType}`}
          confirmLabel="Reject and request recollection"
          tone="danger"
          onConfirm={() => go('REJECTED', reason)}
          onCancel={() => setRejecting(false)}
        >
          <SelectField label="Reason" value={reason} onChange={setReason}>
            {REJECT_REASONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </SelectField>
        </ConfirmDialog>
      )}
    </Card>
  );
}

const REJECT_REASONS: Array<[string, string]> = [
  ['HAEMOLYSED', 'Haemolysed'],
  ['INSUFFICIENT_VOLUME', 'Insufficient volume'],
  ['CLOTTED', 'Clotted'],
  ['MISLABELLED', 'Unlabelled or mislabelled'],
  ['CONTAMINATED', 'Contaminated or wrong tube'],
  ['EXPIRED_IN_TRANSIT', 'Received too late for the test'],
];

function transition(id: string, to: string, reason?: string) {
  return api.post(`/lab/samples/${id}/transition`, { to, reason });
}

function when(iso: string | null): string {
  // The product's one date style ("25 Sept 2026, 10:09 pm"), not the
  // browser's own locale, which read "Sep 25, 10:09 PM" on the same page.
  return iso ? formatDateTime(iso) : '—';
}

export function subjectLine(s: { sex: string | null; dateOfBirth: string | null }): string {
  return [s.sex ? s.sex.charAt(0) + s.sex.slice(1).toLowerCase() : null, s.dateOfBirth ? age(s.dateOfBirth) : null]
    .filter(Boolean).join(' · ');
}

/**
 * A tube's state as an operational tone. Never a fitness tone: a rejected
 * tube is a workflow problem, not an unfit worker, and borrowing the unfit
 * red for it teaches the reader that red means two things.
 */
export function sampleTone(status: string): Tone {
  switch (status) {
    case 'RELEASED': return 'success';
    case 'REJECTED': return 'danger';
    case 'RECOLLECT_REQUESTED': return 'warning';
    case 'RESULTED': case 'VALIDATED': return 'info';
    default: return 'pending';
  }
}

export function age(dob: string | null): string {
  if (!dob) return '';
  const d = new Date(dob);
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
  return `${years} years`;
}
